import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';
import {
  AnonymizedData,
  ApprovedData,
  CreatedData,
  PendingData,
  RejectedData,
  TAudit,
} from './audit-types';
import { TEmployeeContract } from './digital-signatures.api';
import { TMedicalExam } from './medical-exams.api';
import { TOffer } from './offers.api';
import { TTaskTemplate } from './tasks-types';

export enum OfferApplicationStatus {
  PENDING = 'PENDING',
  WITHDRAWN = 'WITHDRAWN',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export type TOfferApplication = {
  offer: TOffer & { _id: string; portalId: string };
  status: OfferApplicationStatus;
  employee: TEmployeeWithProfile;
  createdAt: Date;
};

export type TAddress = {
  country?: string;
  district?: string;
  county?: string;
  parish?: string;
  location?: string;
  zipcode?: string;
  completeAddress?: string;
};

export enum Gender {
  MALE = 'Male',
  FEMALE = 'Female',
  OTHER = 'Other',
}

export enum TEmployeeStatus {
  CANDIDATE = '1',
  ACTIVE = '2',
  INACTIVE = '3',
  EXCLUDED = '4',
  RS = '5',
  SUSPENDED = '6',
  REGISTERED = '7',
  ANONYMIZED = '8',
}

export enum TAcademicQualification {
  MENOS_DE_QUATRO_ANOS = 1,
  QUARTO_ANO = 2,
  SEXTO_ANO = 3,
  NONO_ANO = 4,
  DECIMO_SEGUNDO_ANO = 5,
  CURSO_PROFISSIONAL = 6,
  CURSO_TECNICO_PROFISSIONAL = 7,
  BACHARELATO = 8,
  LICENCIATURA = 9,
  MESTRADO = 10,
  DOUTORAMENTO = 11,
  POS_GRADUACAO = 12,
}

export type TScheduleAvailability = {
  fulltimeDay: boolean;
  fulltimeNight: boolean;
  parttimeMorning: boolean;
  parttimeAfternoon: boolean;
  parttimeEvening: boolean;
  parttimeWeekend: boolean;
};

export type TJobPreferences = {
  name: string;
  portalId: string;
  professionalArea: string;
};

export type TLocation = {
  codigo: string;
  nome: string;
  conselho: string;
  distrito: string;
};

export type TDocumentation = {
  type: string;
  expirationDate: Date;
  number: string;
};

export enum CivilStatus {
  SINGLE = 'Single',
  MARRIED = 'Married',
  DIVORCED = 'Divorced',
  WIDOWED = 'Widowed',
  UNION = 'Union',
}

export type TProfessionalExperience = {
  title?: string;
  startDate?: Date;
  endDate?: Date;
  company?: string;
  location?: string;
  description?: string;
};

export type TFormation = {
  course?: string;
  startDate?: Date;
  endDate?: Date;
  institution?: string;
};

export type TLanguage = {
  name: string;
  level: string;
};

export type TUserDocumentFile = {
  _id: string;
  name: string;
  type: string;
  link?: string;
  visibleToUser?: boolean;
  fixed?: boolean;
  uploadedAt?: Date;
  uploadedBy?: string;
};

export type TLegacyDocument = {
  name: string;
  size: number;
  mtime: Date;
  diskPath: string;
  url: string;
};

export type TLegacyDocuments = {
  employeeId: string;
  employeeCode: string;
  basePath: string;
  categories: {
    contracts: {
      label: string;
      dir: string;
      files: TLegacyDocument[];
    };
    eam: {
      label: string;
      dir: string;
      files: TLegacyDocument[];
    };
    medical: {
      label: string;
      dir: string;
      files: TLegacyDocument[];
    };
    other: {
      label: string;
      dir: string;
      files: TLegacyDocument[];
    };
    pawns: {
      label: string;
      dir: string;
      files: TLegacyDocument[];
    };
    personal: {
      label: string;
      dir: string;
      files: TLegacyDocument[];
    };
  };
};

export type TBankInfo = {
  iban: string;
  bankName: string;
  confirmed: boolean;
};

export type TEmployeeProfile = {
  name: string;
  email: string;
  niss: string;
  //firstName: string;
  //lastName: string;
  dob: string; // date of birth
  cellphone: {
    indicative: string;
    number: string;
  };
  address: TAddress;
  genre: Gender;
  consentOffersMarketing: boolean;
  declarationTruthfulness: boolean;
  consentDataProcessing: boolean;
  consentMarketing: boolean;
  acknowledgePrivacyPolicy: boolean;
  scheduleAvailability: TScheduleAvailability;
  jobPreferences: TJobPreferences[] | string[];
  locationPreferences: TLocation[] | string[];
  mayWorkAnywhere: boolean;
  cv?: string;
  cvAnalysis?: string;
  hasDriversLicense?: boolean;
  driversLicense?: string[];
  documentation: TDocumentation[];
  dependents?: number;
  dependentsDef?: number;
  holders?: number;
  partnerDef?: boolean;
  youthIRS?: boolean;
  youthIRSYear?: number;
  irsTypeId?: number;
  incapacity60?: boolean | null;
  incapacityType?: string | null;
  verified?: boolean;
  lastLogin?: Date;
  civilStatus?: CivilStatus;
  nationality?: string;
  geographicalArea?: number;
  hasNoProfessionalExperience?: boolean;
  experience: TProfessionalExperience[];
  formation: TFormation[];
  academicQualification?: TAcademicQualification;
  languages: TLanguage[];
  skills: { name: string }[];
  behaviorSkills: { name: string }[];
  documents: TUserDocumentFile[];
  bankInfo?: TBankInfo;
  allowCVProcessing?: boolean;
};

export type TEvolveInfo = {
  mealCardNumber?: string;
  irsCategoryId?: number;
  regimeTypeId?: number;
  //geographicalArea?: number;
  excludedCause?: string;
  validated?: boolean;
  anonymizeAt?: Date;
  anonymizedBy?: number;
};

export type TModifiedBy = {
  id: string;
  name: string;
  profileId: number;
  agencyId: number;
  agency: {
    id: number;
    name: string;
    acronym: string;
  };
  isAdmin?: boolean;
};

export type TEmployeeWithProfile = {
  id: string;
  portalId: string;
  code: string;
  nif: string;
  profile: TEmployeeProfile;
  otherInfo?: TEvolveInfo;
  photo?: { link: string };
  status?: TEmployeeStatus;
  flags?: {
    hasContracts?: boolean;
    hasPayslips?: boolean;
    hasPawns?: boolean;
    hasMedicalLeaves?: boolean;
    hasInitiatives?: boolean;
    hasInterviews?: boolean;
    //hasPendingUserUpdate: boolean;
    hasOfferApplications?: boolean;
    hasMedicalAptitude?: boolean;
    offerApplications?: { offer: TOffer; employee: TEmployeeWithProfile }[];
    hasOldSiteDocuments?: boolean;
    oldSiteDocuments?: string[];
  };
  hasPendingChanges?: boolean;
  summary?: string;
  modifiedAt?: Date;
  modifiedBy?: TModifiedBy;
  updatedAt?: Date;
  createdAt?: Date;
};

export type TEmployeeAudit = TAudit & {
  data:
    | PendingData
    | ApprovedData
    | RejectedData
    | CreatedData
    | AnonymizedData;
};

export type SyncStatus = {
  status: 'ok' | 'error';
  startedAt: string;
  finishedAt: string;
  message?: string;
  error?: string;
  stack?: string;
};

export type StageStatus = 'ok' | 'skipped' | 'error';

interface StageResult {
  status: StageStatus;
  message?: string;
  error?: string; // safe message for API
  stack?: string; // optional; you may omit from API if sensitive
  startedAt: string;
  finishedAt?: string;
}

interface EmployeeForceSyncResult {
  nif: number;
  gett: StageResult;
  mysql: StageResult;
}

interface ForceSyncSummary {
  total: number;
  succeeded: number; // both stages ok
  failed: number; // any stage errored
}

export type SyncEmployeeResponse = {
  success: boolean; // true only if ALL nifs had both stages ok
  startedAt: string;
  finishedAt: string;
  summary: ForceSyncSummary;
  results: EmployeeForceSyncResult[];
  type: 'force-sync-employees';
};

export type TWebsiteUserLink = {
  type: string;
  label: string;
  url: string;
};

export type TWebsiteUserLinksResponse = {
  type: 'website-user-links';
  nif: string;
  name?: string;
  email?: string;
  links: TWebsiteUserLink[];
};

export type TActiveEmployeeWithPendingChanges = {
  type: string;
  id: string;
  employeeNumber: string;
  name: string | null;
  mongoId: string;
  changes: Record<string, { old: unknown; new: unknown }>;
  /** Unix ms — last candidate save on pending deferred audit (`data.timestamps` last entry). */
  lastChangeAt: number | null;
  lastActiveContract: {
    costCenter: {
      id: string;
      reference: string;
      name: string;
    } | null;
    agency: {
      id: string;
      acronym: string;
      name: string;
    } | null;
  } | null;
};

export const employeesApi = createApi({
  reducerPath: 'employees',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_EMPLOYEES_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Employee', 'Documents', 'Employees', 'Audits'],
  endpoints: (builder) => ({
    getEmployees: builder.query<Response<TEmployeeWithProfile>, TQueryParams>({
      providesTags: ['Employees'],
      query: (params) => formatQueryParams(params, 'advanced-search'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TEmployeeWithProfile>(
          res as JsonApiResponseDto<TEmployeeWithProfile>,
          meta,
          args,
          true,
        ),
    }),

    getActiveEmployeesPendingChanges: builder.query<
      Response<TActiveEmployeeWithPendingChanges>,
      TQueryParams
    >({
      providesTags: ['Employees', 'Audits'],
      query: (params) =>
        formatQueryParams(params, 'active-with-pending-changes'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TActiveEmployeeWithPendingChanges>(
          res as JsonApiResponseDto<TActiveEmployeeWithPendingChanges>,
          meta,
          args,
          true,
        ),
    }),

    createEmployee: builder.mutation<
      Response<TEmployeeWithProfile>,
      { employee: any }
    >({
      invalidatesTags: ['Employees'],
      query: ({ employee }) => ({
        url: 'create',
        method: 'POST',
        body: employee,
      }),
      transformResponse: parseJsonApiResponse<TEmployeeWithProfile>,
    }),

    getEmployee: builder.query<
      Response<TEmployeeWithProfile>,
      TQueryParams & { employeeId: string }
    >({
      providesTags: ['Employee'],
      query: ({ employeeId, populate }) => {
        return formatQueryParams({ populate }, employeeId);
      },
      transformResponse: parseJsonApiResponse<TEmployeeWithProfile>,
    }),
    getEmployeeProfilePicture: builder.query<
      string | null,
      { employeeId: string }
    >({
      query: ({ employeeId }) => ({
        url: `${employeeId}/photo`,
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (data) => {
        if (data) {
          return URL.createObjectURL(data as Blob);
        }
        return null;
      },
    }),
    getEmployeeAudits: builder.query<
      Response<TEmployeeAudit>,
      TQueryParams & { employeeId: string }
    >({
      providesTags: ['Audits'],
      query: ({ employeeId, ...params }) =>
        formatQueryParams({ ...params }, `${employeeId}/audit`),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TEmployeeAudit>(
          res as JsonApiResponseDto<TEmployeeAudit>,
          meta,
          args,
          true,
        ),
    }),
    acceptPendingAudit: builder.mutation<
      Response<TEmployeeWithProfile>,
      { employeeId: string; body?: { fieldsToAccept?: any[] } }
    >({
      invalidatesTags: ['Audits', 'Employee'],
      query: ({ employeeId, body }) => ({
        url: `${employeeId}/accept-pending-changes`,
        method: 'POST',
        body,
      }),
    }),
    rejectPendingAudit: builder.mutation<
      Response<TEmployeeWithProfile>,
      { employeeId: string; reason: string }
    >({
      invalidatesTags: ['Audits'],
      query: ({ employeeId, reason }) => ({
        url: `${employeeId}/reject-pending-changes`,
        method: 'POST',
        body: { reason },
      }),
    }),
    updateEmployee: builder.mutation<
      Response<TEmployeeWithProfile>,
      {
        id: string;
        profile?: Partial<TEmployeeProfile>;
        otherInfo?: Partial<TEvolveInfo>;
      }
    >({
      invalidatesTags: ['Employee', 'Employees'],
      query: (employee) => ({
        url: employee.id,
        method: 'PUT',
        body: employee,
      }),
      //transformResponse: parseJsonApiResponse<TEmployeeWithProfile>,
    }),
    getEmployeeDocuments: builder.query<
      Response<TUserDocumentFile>,
      { employeeId: string }
    >({
      providesTags: ['Documents'],
      query: ({ employeeId }) => ({
        url: `${employeeId}/documents`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TUserDocumentFile>,
    }),
    getEmployeeDocument: builder.mutation({
      queryFn: async (
        { employeeId, documentId },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `${employeeId}/documents/${documentId}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);

          hiddenElement.href = blobPDF;
          hiddenElement.target = '_blank';

          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
    getEmployeeLegacyDocuments: builder.query<
      Response<TLegacyDocuments>,
      { employeeId: string }
    >({
      providesTags: ['Documents'],
      query: ({ employeeId }) => ({
        url: `${employeeId}/legacy-documents`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TLegacyDocuments>,
    }),
    getEmployeeLegacyDocument: builder.mutation({
      queryFn: async (
        { employeeId, category, fileName },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `${employeeId}/legacy-documents/${category}/${fileName}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);

          hiddenElement.href = blobPDF;
          hiddenElement.target = '_blank';

          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),

    uploadEmployeeDocument: builder.mutation({
      invalidatesTags: ['Employee'],
      query: ({ employeeId, file, data }) => {
        const document = file[0];
        const body = new FormData();

        body.append('document', document.file);
        body.append('type', data.type?.value ?? '');
        body.append('originalName', document.filename);
        body.append('visibleToUser', data.visibleToUser?.value ?? false);
        body.append('fixed', data.fixed?.value ?? false);
        return {
          url: `${employeeId}/documents`,
          method: 'POST',
          body,
        };
      },
    }),
    deleteEmployeeDocument: builder.mutation({
      invalidatesTags: ['Employee'],
      query: ({ employeeId, documentId, reason }) => ({
        url: `${employeeId}/documents/${documentId}`,
        method: 'DELETE',
        body: { reason },
      }),
    }),
    acceptEmployee: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId }) => ({
        url: `${employeeId}/accept`,
        method: 'POST',
      }),
      transformResponse: parseJsonApiResponse<TEmployeeWithProfile>,
    }),
    excludeEmployee: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId, body }) => ({
        url: `${employeeId}/exclude`,
        method: 'POST',
        body,
      }),
    }),
    changeEmployeeStatus: builder.mutation({
      invalidatesTags: ['Employee', 'Employees', 'Audits'],
      query: ({ employeeId, status, excludedCause }) => ({
        url: `${employeeId}/status`,
        method: 'PATCH',
        body: { status, excludedCause },
      }),
    }),
    anonymizeEmployee: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId }) => ({
        url: `${employeeId}/anonymize`,
        method: 'POST',
      }),
    }),
    updateEmployeeNif: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId, body }) => ({
        url: `${employeeId}/nif`,
        method: 'PUT',
        body,
      }),
    }),
    validateEmployee: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId }) => ({
        url: `${employeeId}/validate`,
        method: 'POST',
      }),
    }),
    rejectEmployee: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId, reason }) => ({
        url: `${employeeId}`,
        method: 'DELETE',
        body: { reason },
      }),
    }),
    getTaskTemplates: builder.query<Response<TTaskTemplate>, TQueryParams>({
      query: (params) => formatQueryParams(params, 'tasks/templates'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TTaskTemplate>(
          res as JsonApiResponseDto<TTaskTemplate>,
          meta,
          args,
          true,
        ),
    }),

    createTask: builder.mutation<
      Response<TTaskTemplate>,
      { employeeId: string; body: any }
    >({
      invalidatesTags: ['Audits'],
      query: ({ employeeId, body }) => ({
        url: `${employeeId}/tasks`,
        method: 'POST',
        body,
      }),
    }),
    createNotification: builder.mutation<
      Response<any>,
      { employeeId: string; body: any }
    >({
      invalidatesTags: ['Audits'],
      query: ({ employeeId, body }) => ({
        url: `${employeeId}/notification`,
        method: 'POST',
        body,
      }),
    }),
    getEmployeeOfferApplications: builder.query<
      Response<TOfferApplication>,
      TQueryParams & { employeeId: string }
    >({
      query: ({ employeeId, ...params }) =>
        formatQueryParams({ ...params }, `${employeeId}/offer-applications`),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TOfferApplication>(
          res as JsonApiResponseDto<TOfferApplication>,
          meta,
          args,
          true,
        ),
    }),
    syncEmployee: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeNifs }) => ({
        url: `sync/employees-from-mongo-to-mysql`,
        method: 'POST',
        body: { nifs: employeeNifs },
      }),
      transformResponse: parseJsonApiResponse<SyncEmployeeResponse>,
    }),
    syncEmployeeWithContracts: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ nif, contractId }) => ({
        url: `sync/employees-with-contract-from-mongo-to-mysql`,
        method: 'POST',
        body: { nif, contractId },
      }),
      transformResponse: parseJsonApiResponse<SyncEmployeeResponse>,
    }),
    syncEmployeeContracts: builder.mutation({
      invalidatesTags: ['Employee', 'Employees'],
      query: ({ employeeId, contractId }) => ({
        url: `sync/employee-contract`,
        method: 'POST',
        body: { employeeId, contractId },
      }),
      transformResponse: parseJsonApiResponse<SyncEmployeeResponse>,
    }),

    getEmployeeContractsById: builder.query<
      Response<TEmployeeContract>,
      TQueryParams & { employeeId: string }
    >({
      query: ({ employeeId, ...params }) =>
        formatQueryParams({ ...params }, `${employeeId}/contracts`),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TEmployeeContract>(
          res as JsonApiResponseDto<TEmployeeContract>,
          meta,
          args,
          true,
        ),
    }),
    getEmployeeMedicalExams: builder.query<
      Response<TMedicalExam>,
      TQueryParams & { employeeId: string }
    >({
      query: ({ employeeId, ...params }) =>
        formatQueryParams({ ...params }, `${employeeId}/medical-exams`),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TMedicalExam>(
          res as JsonApiResponseDto<TMedicalExam>,
          meta,
          args,
          true,
        ),
    }),
    getWebsiteUserLinks: builder.query<
      Response<TWebsiteUserLinksResponse>,
      { nif: string }
    >({
      query: ({ nif }) => ({
        url: 'website-users/links',
        params: { nif },
      }),
      transformResponse: parseJsonApiResponse<TWebsiteUserLinksResponse>,
    }),
  }),
});

export const {
  useCreateEmployeeMutation,
  useGetEmployeesQuery,
  useGetActiveEmployeesPendingChangesQuery,
  useGetEmployeeQuery,
  useGetEmployeeProfilePictureQuery,
  useGetEmployeeAuditsQuery,
  useAcceptPendingAuditMutation,
  useRejectPendingAuditMutation,
  useUpdateEmployeeMutation,
  useGetEmployeeDocumentsQuery,
  useGetEmployeeDocumentMutation,
  useGetEmployeeLegacyDocumentsQuery,
  useGetEmployeeLegacyDocumentMutation,
  useUploadEmployeeDocumentMutation,
  useDeleteEmployeeDocumentMutation,
  useAcceptEmployeeMutation,
  useExcludeEmployeeMutation,
  useChangeEmployeeStatusMutation,
  useAnonymizeEmployeeMutation,
  useUpdateEmployeeNifMutation,
  useValidateEmployeeMutation,
  useRejectEmployeeMutation,
  useGetTaskTemplatesQuery,
  useCreateTaskMutation,
  useCreateNotificationMutation,
  useGetEmployeeOfferApplicationsQuery,
  useSyncEmployeeMutation,
  useSyncEmployeeWithContractsMutation,
  useSyncEmployeeContractsMutation,
  useGetEmployeeContractsByIdQuery,
  useGetEmployeeMedicalExamsQuery,
  useLazyGetWebsiteUserLinksQuery,
} = employeesApi;
